import { useEffect, useState } from 'react';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { CHAIN_IDS_SUPPORT_GASLESS, SUPPORTED_CHAINS } from '@/constants/chain';

type SlippagePreset = 0.001 | 0.0025 | 0.005 | 0.0075 | 0.01; // Percentages
export type GasFeePreset = 'suggested' | 'custom';

export const GAS_FEE_PRESETS: GasFeePreset[] = ['suggested', 'custom'];
const DEFAULT_GAS_FEE_PRESET: GasFeePreset = 'suggested';
export const SLIPPAGE_PRESETS: SlippagePreset[] = [0.001, 0.0025, 0.005, 0.0075, 0.01];
export const DEFAULT_SLIPPAGE_PERCENT = 0.005;

interface MarketTradeSettings {
  isGasless: boolean;
  gasFeePreset: GasFeePreset;
  baseFee: string | undefined; // gwei. eip1559 chains
  priorityFee: string | undefined; // gwei. eip1559 chains
  gasPrice: string | undefined; // gwei. type 1
  slippage: number;
  isSlippageCustom: boolean;
}

interface MarketTradeSettingsStore {
  /** The current trade settings, divided by chain id */
  settings: Record<number, MarketTradeSettings>;
  /** Whether the user has altered the settings on a chain id  */
  hasAlteredSettings: (chainId: number) => boolean;
  /** Resets the settings to their default values for chain id */
  resetSettings: (chainId: number) => void;
  /** Sets the trade settings for chain id */
  setSettings: (chainId: number, settings: Partial<MarketTradeSettings>) => void;
  /** Sets whether the trade is gasless on a chain id */
  setIsGasless: (chainId: number, isGasless: boolean) => void;
}

const INITIAL_SETTINGS: MarketTradeSettings = {
  isGasless: true,
  gasFeePreset: DEFAULT_GAS_FEE_PRESET,
  baseFee: undefined,
  priorityFee: undefined,
  gasPrice: undefined,
  slippage: DEFAULT_SLIPPAGE_PERCENT,
  isSlippageCustom: false,
};

const initialSettingsForChainId = (chainId: number) => ({
  ...INITIAL_SETTINGS,
  isGasless: CHAIN_IDS_SUPPORT_GASLESS.includes(chainId),
});

// store
const useInternalMarketTradeSettingsStore = createWithEqualityFn<MarketTradeSettingsStore>()(
  persist(
    (set, get) => ({
      // state
      settings: Object.fromEntries(
        SUPPORTED_CHAINS.map((chain) => chain.id).map((id) => [id, initialSettingsForChainId(id)]),
      ),
      // methods
      hasAlteredSettings: (chainId: number) => {
        const currentSettings = get().settings[chainId] ?? {};
        return (
          // need to test each property for undefined because of the way INITIAL_SETTINGS is typed
          currentSettings.isGasless !== INITIAL_SETTINGS.isGasless ||
          currentSettings.gasFeePreset !== INITIAL_SETTINGS.gasFeePreset ||
          currentSettings.baseFee !== INITIAL_SETTINGS.baseFee ||
          currentSettings.priorityFee !== INITIAL_SETTINGS.priorityFee ||
          currentSettings.gasPrice !== INITIAL_SETTINGS.gasPrice ||
          currentSettings.slippage !== INITIAL_SETTINGS.slippage ||
          currentSettings.isSlippageCustom !== INITIAL_SETTINGS.isSlippageCustom
        );
      },

      resetSettings: (chainId: number) =>
        set((state) => {
          let newSettings = { ...state.settings };
          newSettings[chainId] = initialSettingsForChainId(chainId);
          return { settings: newSettings };
        }),

      setSettings: (chainId: number, settings: Partial<MarketTradeSettings>) =>
        set((state) => {
          let newSettings = { ...state.settings };
          newSettings[chainId] = { ...newSettings[chainId], ...settings };
          return { settings: newSettings };
        }),

      setIsGasless: (chainId: number, isGasless: boolean) =>
        set((state) => {
          let newSettings = { ...state.settings };
          if (!newSettings[chainId]) {
            newSettings[chainId] = initialSettingsForChainId(chainId);
          }
          newSettings[chainId].isGasless = isGasless;
          return { settings: newSettings };
        }),
    }),
    {
      name: 'matcha-market-trade-settings-storage',
      // to prevent zustand to automatically rehydrate
      skipHydration: true,
    },
  ),
  shallow,
);

/**
 * This function is a wrapper for the standard exported hook provided by zustand.
 * The hook ensures that the zustand store is only hydrated after the initial render cycle, to
 * prevent hydration mismatches
 */
export function useMarketTradeSettingsStore<T>(
  selector: (state: MarketTradeSettingsStore) => T = (x) => x as T,
) {
  const [rehydrated, setIsRehydrated] = useState(false);

  useEffect(() => {
    if (!rehydrated) {
      useInternalMarketTradeSettingsStore.persist.rehydrate();
      setIsRehydrated(true);
    }
  }, [rehydrated]);

  return useInternalMarketTradeSettingsStore(selector);
}

// Selectors
export const settingsSelector = (chainId: number) => (state: MarketTradeSettingsStore) => ({
  settings: state.settings[chainId],
  setSettings: (settings: Partial<MarketTradeSettings>) => state.setSettings(chainId, settings),
});

export const resetSettingsSelector = (chainId: number) => (state: MarketTradeSettingsStore) => () =>
  state.resetSettings(chainId);

export const gaslessSelectors = (chainId: number) => (state: MarketTradeSettingsStore) => {
  if (!state.settings[chainId]) {
    state.setSettings(chainId, initialSettingsForChainId(chainId));
  }
  return {
    isGasless: state.settings[chainId]?.isGasless ?? CHAIN_IDS_SUPPORT_GASLESS.includes(chainId),
    setIsGasless: (isGasless: boolean) => state.setIsGasless(chainId, isGasless),
  };
};

export const slippageSelector = (chainId: number) => (state: MarketTradeSettingsStore) => {
  if (!state.settings[chainId]) {
    state.setSettings(chainId, initialSettingsForChainId(chainId));
  }
  return {
    slippage: state.settings[chainId].slippage,
    isSlippageCustom: state.settings[chainId].isSlippageCustom,
    setSettings: (settings: Partial<MarketTradeSettings>) => state.setSettings(chainId, settings),
  };
};

export const hasAlteredSettingsSelector = (chainId: number) => (state: MarketTradeSettingsStore) =>
  state.hasAlteredSettings(chainId);
